import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  makeStyles,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import SendIcon from '@material-ui/icons/Send';
import {
  importRequirements,
  importRequirementTexts,
  type ImportRequirementResult,
} from './api/api';
import { extractRequirementTexts } from './utils/csv';
import { useSharedStyles } from './styles/shared';
import { ApiError } from '../../utils/apiClient';

interface ImportFormProps {
  projectId: number;
  onImport: (results: ImportRequirementResult[]) => void;
  onError?: (error: string) => void;
}

const useStyles = makeStyles(theme => ({
  dropzone: {
    border: '2px dashed',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    textAlign: 'center',
    cursor: 'pointer',
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  dropzoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
  dropzoneInactive: {
    borderColor: theme.palette.divider,
    backgroundColor: 'transparent',
  },
  uploadIcon: {
    fontSize: 40,
    marginBottom: theme.spacing(1),
  },
  importButton: {
    marginTop: theme.spacing(2),
    width: '100%',
  },
}));

const ImportForm: React.FC<ImportFormProps> = ({
  projectId,
  onImport,
  onError,
}) => {
  const shared = useSharedStyles();
  const classes = useStyles();
  const { t } = useTranslation('requirement-doctor');
  const [activeTab, setActiveTab] = useState(0);
  // Starts empty: the sample is only shown as the placeholder, so importing
  // without typing anything is refused instead of registering the sample.
  const [textInput, setTextInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [csvTexts, setCsvTexts] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reportError = (err: unknown) => {
    let detail: string;
    if (err instanceof ApiError) {
      detail = `${err.message} (${err.code})`;
    } else if (err instanceof Error) {
      detail = err.message;
    } else {
      detail = String(err);
    }
    const message = t('s0.errors.generic', { detail });
    setError(message);
    onError?.(message);
  };

  // Read + parse a selected/dropped CSV file into requirement texts.
  const handleCsvFile = async (file: File) => {
    setCsvFileName(null);
    setCsvTexts([]);
    try {
      setError(null);
      const content = await file.text();
      const texts = extractRequirementTexts(content);

      if (texts.length === 0) {
        setError(t('s0.validation.empty'));
        return;
      }
      if (texts.length > 100) {
        setError(t('s0.validation.tooMany'));
        return;
      }

      setCsvFileName(file.name);
      setCsvTexts(texts);
    } catch (err) {
      reportError(err);
    }
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      void handleCsvFile(file);
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      void handleCsvFile(file);
    }
  };

  const handleImport = async () => {
    try {
      setLoading(true);
      setError(null);

      if (activeTab === 0) {
        // Text import
        const lines = textInput
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0);

        if (lines.length === 0) {
          setError(t('s0.validation.empty'));
          return;
        }

        if (lines.length > 100) {
          setError(t('s0.validation.tooMany'));
          return;
        }

        const response = await importRequirements(
          projectId,
          'text',
          lines.join('\n'),
        );
        const results = response.results ?? [];

        setSuccessMessage(
          t('common.messages.imported', { count: results.length }),
        );
        setTextInput('');
        onImport(results);
      } else {
        // CSV import
        if (csvTexts.length === 0) {
          setError(t('s0.validation.empty'));
          return;
        }

        const response = await importRequirementTexts(projectId, csvTexts);
        const results = response.results ?? [];

        setSuccessMessage(
          t('common.messages.imported', { count: results.length }),
        );
        setCsvFileName(null);
        setCsvTexts([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        onImport(results);
      }
    } catch (err) {
      reportError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card className={shared.mb2}>
        <CardContent>
          <Typography variant="h6" className={shared.mb2}>
            💻 {t('s0.title')}
          </Typography>

          {error && (
            <Alert
              severity="error"
              className={shared.mb2}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          <Tabs
            value={activeTab}
            onChange={(_e, v) => setActiveTab(v)}
            className={shared.mb2}
          >
            <Tab label={t('s0.tabs.text')} />
            <Tab label={t('s0.tabs.csv')} />
          </Tabs>

          {activeTab === 0 && (
            <Box>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={textInput}
                onChange={e => setTextInput(e.target.value)}
                placeholder={`${t('s0.textInput.placeholder')}\n${t(
                  's0.textInput.sampleText',
                )}`}
                variant="outlined"
                className={shared.mb2}
                disabled={loading}
              />
              <Button
                variant="contained"
                endIcon={
                  loading ? <CircularProgress size={20} /> : <SendIcon />
                }
                onClick={handleImport}
                fullWidth
                disabled={loading}
              >
                {loading
                  ? t('s0.buttons.importing')
                  : `📤 ${t('s0.buttons.import')}`}
              </Button>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={onFileInputChange}
                data-testid="csv-file-input"
              />
              <Box
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={e => {
                  e.preventDefault();
                  // Only deactivate when leaving the dropzone itself, not a child element.
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setDragActive(false);
                  }
                }}
                onDrop={onDrop}
                className={`${classes.dropzone} ${
                  dragActive ? classes.dropzoneActive : classes.dropzoneInactive
                }`}
              >
                <CloudUploadIcon className={classes.uploadIcon} />
                <Typography variant="body2" color="textSecondary">
                  {csvFileName
                    ? t('s0.csvUpload.detected', {
                        file: csvFileName,
                        count: csvTexts.length,
                      })
                    : t('s0.csvUpload.placeholder')}
                </Typography>
              </Box>
              <Button
                variant="contained"
                endIcon={
                  loading ? <CircularProgress size={20} /> : <SendIcon />
                }
                className={classes.importButton}
                onClick={handleImport}
                disabled={loading || csvTexts.length === 0}
              >
                {loading
                  ? t('s0.buttons.importing')
                  : `📤 ${t('s0.buttons.import')}`}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </>
  );
};

export default ImportForm;
